import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis } from "recharts";
import { Card, SectionTitle, TornaHome } from "@/components/Kpi";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { eurShort, MESI, num } from "@/lib/format";

const BLU = "#14284B";
const ROSSO = "#C8102E";
const VERDE = "#15803D";
const GRIGIO = "#94A3B8";
const TEAL = "#0F766E";

const LINEE = [
  { key: "corto", label: "Canale Corto", figlio: false, breve: "Corto" },
  { key: "corto_non_garantiti", label: "Non garantiti", figlio: true, breve: "Non gar." },
  { key: "corto_garantiti", label: "Garantiti", figlio: true, breve: "Garantiti" },
  { key: "consumer", label: "Consumer (Canale Lungo)", figlio: false, breve: "Lungo" },
  { key: "consumer_car", label: "Car", figlio: true, breve: "Car" },
  { key: "consumer_pos", label: "POS", figlio: true, breve: "POS" },
  { key: "accordi", label: "Accordi (BancoPosta)", figlio: false, breve: "Accordi" },
];
const BREVE = Object.fromEntries(LINEE.map((l) => [l.key, l.breve]));

const VUOTA = { volumi_mln: "", var_budget_perc: "", var_yoy_perc: "" };
const bozzaVuota = (anno, mese) => ({
  anno, mese, totale: { ...VUOTA },
  linee: Object.fromEntries(LINEE.map((l) => [l.key, { ...VUOTA }])),
  rischio: { r30_perc: "", r30_delta_bps: "", r90_perc: "", r90_delta_bps: "" },
  testi: { intro: "", flash: "", progetti: "" },
});

const testo = (v) => (v === null || v === undefined ? "" : String(v));
const vuoto = (v) => v === null || v === undefined;
const pp = (v) => (vuoto(v) ? "—" : `${v > 0 ? "+" : ""}${num(v, 1)}%`);
const ppp = (v) => (vuoto(v) ? "—" : `${v > 0 ? "+" : ""}${num(v, 1)} pp`);
const tinta = (v) => (vuoto(v) ? "text-slate-400" : v >= 0 ? "text-emerald-700" : "text-red-600");

/** Mette in grassetto i valori numerici del commento. */
const NUMERI = /((?:[+-]\s?)?\d[\d.,]*\s?(?:€M|€|%|bps|p\.p\.|punti percentuali|Mln|mesi)?)/g;
function TestoConNumeri({ children, testid }) {
  if (!children) return null;
  return (
    <div className="whitespace-pre-line text-sm leading-relaxed text-slate-800" data-testid={testid}>
      {String(children).split(NUMERI).map((pezzo, i) => (
        i % 2 === 1
          ? <strong key={i} className="font-bold text-slate-900">{pezzo}</strong>
          : <span key={i}>{pezzo}</span>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ articolo del mese

function RigaConfronto({ r }) {
  const figlio = !["corto", "consumer"].includes(r.key);
  return (
    <tr className="border-b border-slate-100" data-testid={`confronto-riga-${r.key}`}>
      <td className={`py-2 pr-3 text-sm ${figlio ? "pl-6 text-slate-600" : "font-semibold text-slate-900"}`}>
        {r.label}
      </td>
      <td className="py-2 px-3 text-right text-sm text-slate-700">
        {vuoto(r.dbe_volumi_mln) ? "—" : <strong>{num(r.dbe_volumi_mln, 1)} €M</strong>}
      </td>
      <td className={`py-2 px-3 text-right text-sm font-bold ${tinta(r.dbe_var_budget_perc)}`}>
        {pp(r.dbe_var_budget_perc)}
      </td>
      <td className="py-2 px-3 text-right text-sm text-slate-700">
        {vuoto(r.nostro_liquidato) ? "—" : <strong>{eurShort(r.nostro_liquidato)}</strong>}
      </td>
      <td className={`py-2 px-3 text-right text-sm font-bold ${tinta(r.nostro_var_budget_perc)}`}>
        {r.nostro_budget ? (
          <>
            {pp(r.nostro_var_budget_perc)}
            {r.nostro_budget_stimato && (
              <span className="text-slate-400"
                title="Budget del mese non caricato: ripartito sui giorni lavorativi">&nbsp;*</span>
            )}
          </>
        ) : <span className="text-xs font-normal text-slate-400">budget non disponibile</span>}
      </td>
      <td className={`py-2 pl-3 text-right text-sm font-bold ${tinta(r.delta_pp)}`}>
        {ppp(r.delta_pp)}
      </td>
    </tr>
  );
}

function TabellaConfronto({ righe }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse">
        <thead>
          <tr className="border-b-2 border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-3 text-left font-semibold">Linea di business</th>
            <th className="py-2 px-3 text-right font-semibold">Deutsche Bank Easy</th>
            <th className="py-2 px-3 text-right font-semibold">D.B. Easy Δ% budget</th>
            <th className="py-2 px-3 text-right font-semibold">DBE Srl</th>
            <th className="py-2 px-3 text-right font-semibold">DBE Srl Δ% budget</th>
            <th className="py-2 pl-3 text-right font-semibold">Differenza</th>
          </tr>
        </thead>
        <tbody>{(righe || []).map((r) => <RigaConfronto key={r.key} r={r} />)}</tbody>
      </table>
    </div>
  );
}

function Articolo({ doc, puoScrivere, onModifica, onElimina, onRigenera }) {
  const [inModifica, setInModifica] = useState(false);
  const [bozza, setBozza] = useState(doc.articolo || "");
  const [rigenerando, setRigenerando] = useState(false);
  const ris = doc.rischio || {};
  useEffect(() => { setInModifica(false); setBozza(doc.articolo || ""); }, [doc]);
  return (
    <Card className="p-5 sm:p-6" data-testid={`articolo-${doc.anno}-${doc.mese}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xl sm:text-2xl font-black tracking-tight" style={{ color: BLU }}>
            {MESI[doc.mese - 1]} {doc.anno}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Fonte: newsletter Deutsche Bank Easy · dati di liquidato, scostamenti sul budget
            {!vuoto(doc.totale?.volumi_mln)
              && ` · sintesi newsletter ${num(doc.totale.volumi_mln, 1)} €M`}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {doc.file_id && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/benchmark/newsletter/${doc.anno}/${doc.mese}/file`}
              target="_blank" rel="noreferrer"
              data-testid={`apri-newsletter-${doc.anno}-${doc.mese}`}
              className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold
                text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900">
              Newsletter originale
            </a>
          )}
          {puoScrivere && (
            <>
              <button type="button" disabled={rigenerando}
                onClick={async () => { setRigenerando(true); await onRigenera(doc); setRigenerando(false); }}
                data-testid={`rigenera-articolo-${doc.anno}-${doc.mese}`}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold
                  text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900
                  disabled:opacity-50">
                {rigenerando ? "Rigenerazione…" : "Rigenera commento"}
              </button>
              <button type="button"
                onClick={() => { setBozza(doc.articolo || ""); setInModifica((v) => !v); }}
                data-testid={`modifica-articolo-${doc.anno}-${doc.mese}`}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold
                  text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900">
                {inModifica ? "Annulla" : "Modifica testo"}
              </button>
              <button type="button" onClick={() => onElimina(doc)}
                data-testid={`elimina-articolo-${doc.anno}-${doc.mese}`}
                className="rounded-full border border-red-200 px-3 py-1 text-xs font-semibold
                  text-red-600 transition-colors hover:bg-red-50">
                Elimina
              </button>
            </>
          )}
        </div>
      </div>

      {inModifica ? (
        <div className="mt-4">
          <textarea value={bozza} onChange={(e) => setBozza(e.target.value)} rows={7}
            data-testid={`textarea-articolo-${doc.anno}-${doc.mese}`}
            className="w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-800
              focus:border-[#14284B] focus:outline-none" />
          <button type="button" data-testid={`salva-articolo-${doc.anno}-${doc.mese}`}
            onClick={async () => { await onModifica(doc, bozza); setInModifica(false); }}
            className="mt-2 rounded-full px-4 py-1.5 text-xs font-bold text-white"
            style={{ background: BLU }}>
            Salva testo
          </button>
        </div>
      ) : (
        <div className="mt-4">
          <TestoConNumeri testid={`testo-articolo-${doc.anno}-${doc.mese}`}>
            {doc.articolo || "Commento non disponibile: i numeri qui sotto restano validi."}
          </TestoConNumeri>
        </div>
      )}

      <div className="mt-5"><TabellaConfronto righe={doc.confronto} /></div>
      {(doc.confronto || []).some((r) => r.nostro_budget_stimato) && (
        <p className="mt-2 text-xs text-slate-500" data-testid="nota-budget-stimato">
          * Budget mensile del Canale Corto non caricato per questo mese: ripartito sui giorni
          lavorativi a partire dal budget annuale. Caricando i budget mensili in
          Impostazioni · Configurazioni il valore diventa quello ufficiale.
        </p>
      )}

      {(!vuoto(ris.r30_perc) || !vuoto(ris.r90_perc)) && (
        <div className="mt-5 rounded-lg bg-slate-50 p-3 text-sm">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">
            Rischio portafoglio consumer Deutsche Bank Easy
          </div>
          <div className="mt-1 text-slate-800">
            30+ a <strong>{num(ris.r30_perc, 2)}%</strong>
            {!vuoto(ris.r30_delta_bps) && <> (<strong>{ris.r30_delta_bps > 0 ? "+" : ""}{num(ris.r30_delta_bps, 0)} bps</strong> sul mese precedente)</>}
            {" · "}90+ a <strong>{num(ris.r90_perc, 2)}%</strong>
            {!vuoto(ris.r90_delta_bps) && <> (<strong>{ris.r90_delta_bps > 0 ? "+" : ""}{num(ris.r90_delta_bps, 0)} bps</strong>)</>}
          </div>
        </div>
      )}
      {doc.testi?.flash && (
        <details className="mt-3 rounded-lg bg-slate-50 p-3">
          <summary className="cursor-pointer text-[11px] uppercase tracking-wide text-slate-500">
            Testo integrale della newsletter
          </summary>
          <div className="mt-2 space-y-2 whitespace-pre-line text-xs text-slate-600">
            {doc.testi.intro && <p>{doc.testi.intro}</p>}
            {doc.testi.flash && <p>{doc.testi.flash}</p>}
            {doc.testi.progetti && <p>{doc.testi.progetti}</p>}
          </div>
        </details>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ DBE Srl contro il mercato

const etichettaPerc = (v) => (vuoto(v) ? "" : `${v > 0 ? "+" : ""}${num(v, 1)}%`);

function Legenda({ voci }) {
  return (
    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5" data-testid="legenda">
      {voci.map((v) => (
        <span key={v.label} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
          <span className="h-3 w-3 rounded-sm" style={{ background: v.colore }} />
          {v.label}
        </span>
      ))}
    </div>
  );
}

function riassuntoBudget(righe) {
  let liq = 0; let bdg = 0; let vol = 0; let bdgDbe = 0;
  for (const r of (righe || []).filter((x) => ["corto", "consumer"].includes(x.key))) {
    if (vuoto(r.dbe_volumi_mln) || vuoto(r.dbe_var_budget_perc)
      || !r.nostro_budget || vuoto(r.nostro_liquidato)) continue;
    liq += r.nostro_liquidato;
    bdg += r.nostro_budget;
    vol += r.dbe_volumi_mln;
    bdgDbe += r.dbe_volumi_mln / (1 + r.dbe_var_budget_perc / 100);
  }
  if (!bdg || !bdgDbe) return null;
  const nostro = (liq / bdg - 1) * 100;
  const dbe = (vol / bdgDbe - 1) * 100;
  return { nostro, dbe, delta: nostro - dbe };
}

function VerdettoBudget({ righe, etichetta, testid }) {
  const r = riassuntoBudget(righe);
  if (!r) return null;
  const meglio = r.delta >= 0;
  return (
    <div className={`h-fit max-w-xl rounded-xl border-2 p-4 ${
      meglio ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
      data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">
        {etichetta} · Canale Corto + Consumer
      </div>
      <div className={`mt-1 text-lg sm:text-xl font-black ${
        meglio ? "text-emerald-800" : "text-red-700"}`}>
        {meglio ? "Facciamo meglio della banca" : "Facciamo peggio della banca"}
      </div>
      <div className="mt-2 text-sm text-slate-700">
        DBE Srl <strong>{pp(Math.round(r.nostro * 100) / 100)}</strong> sul budget, Deutsche Bank
        Easy <strong>{pp(Math.round(r.dbe * 100) / 100)}</strong>: differenza di{" "}
        <strong>{ppp(Math.round(r.delta * 100) / 100)}</strong>.
      </div>
    </div>
  );
}

function GraficoSettori({ righe, testid }) {
  const dati = (righe || [])
    .filter((r) => !vuoto(r.dbe_var_budget_perc) && !vuoto(r.nostro_var_budget_perc))
    .map((r) => ({ nome: BREVE[r.key] || r.label, "Deutsche Bank Easy": r.dbe_var_budget_perc,
      "DBE Srl": r.nostro_var_budget_perc }));
  if (!dati.length) {
    return <p className="text-sm text-slate-500" data-testid={`${testid}-vuoto`}>
      Nessun dato confrontabile.</p>;
  }
  const valori = dati.flatMap((d) => [d["Deutsche Bank Easy"], d["DBE Srl"]]);
  // margine sopra e sotto: le etichette delle barre non devono finire sull'asse
  const basso = Math.floor((Math.min(...valori, 0) - 12) / 10) * 10;
  const alto = Math.ceil((Math.max(...valori, 0) + 12) / 10) * 10;
  const dominio = [basso, alto];
  return (
    <div>
      <div className="h-72" data-testid={testid}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dati} margin={{ top: 20, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="nome" tick={{ fontSize: 11, fill: "#475569" }} interval={0} />
            <YAxis tick={{ fontSize: 11, fill: "#475569" }} unit="%" domain={dominio} />
            <Tooltip formatter={(v) => `${num(v, 1)}%`} />
            <ReferenceLine y={0} stroke="#94A3B8" />
            <Bar dataKey="Deutsche Bank Easy" fill={BLU} radius={[3, 3, 0, 0]}>
              <LabelList dataKey="Deutsche Bank Easy" position="top" formatter={etichettaPerc}
                style={{ fontSize: 10, fill: BLU, fontWeight: 700 }} />
            </Bar>
            <Bar dataKey="DBE Srl" radius={[3, 3, 0, 0]}>
              {dati.map((d) => (
                <Cell key={d.nome}
                  fill={d["DBE Srl"] >= d["Deutsche Bank Easy"] ? VERDE : ROSSO} />
              ))}
              <LabelList dataKey="DBE Srl" position="top" formatter={etichettaPerc}
                style={{ fontSize: 10, fill: "#334155", fontWeight: 700 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Legenda voci={[
        { label: "Deutsche Bank Easy", colore: BLU },
        { label: "DBE Srl · meglio della banca", colore: VERDE },
        { label: "DBE Srl · peggio della banca", colore: ROSSO },
      ]} />
    </div>
  );
}

function GraficoCrescita({ mesi }) {
  const dati = (mesi || []).map((m) => ({
    nome: m.mese_label.slice(0, 3), "DBE Srl": m.dbe_var_perc,
    "Credito al consumo Italia": m.mercato_var_perc }));
  if (!dati.length) {
    return <p className="text-sm text-slate-500" data-testid="crescita-vuoto">
      Nessun mese confrontabile: serve il dato Banca d'Italia di entrambi gli anni.</p>;
  }
  return (
    <div>
      <div className="h-72" data-testid="grafico-crescita">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dati} margin={{ top: 20, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="nome" tick={{ fontSize: 11, fill: "#475569" }} interval={0} />
            <YAxis tick={{ fontSize: 11, fill: "#475569" }} unit="%" />
            <Tooltip formatter={(v) => `${num(v, 1)}%`} />
            <ReferenceLine y={0} stroke="#94A3B8" />
            <Bar dataKey="Credito al consumo Italia" fill={GRIGIO} radius={[3, 3, 0, 0]} />
            <Bar dataKey="DBE Srl" fill={TEAL} radius={[3, 3, 0, 0]}>
              <LabelList dataKey="DBE Srl" position="top" formatter={etichettaPerc}
                style={{ fontSize: 10, fill: TEAL, fontWeight: 700 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Legenda voci={[
        { label: "Credito al consumo in Italia (Banca d'Italia)", colore: GRIGIO },
        { label: "DBE Srl", colore: TEAL },
      ]} />
    </div>
  );
}

function GraficoProgressivo({ mesi }) {
  const dati = (mesi || []).map((m) => ({
    nome: m.mese_label.slice(0, 3), "DBE Srl": m.dbe_var_progr_perc,
    "Credito al consumo Italia": m.mercato_var_progr_perc }));
  if (!dati.length) return null;
  return (
    <div>
      <div className="h-72" data-testid="grafico-progressivo">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dati} margin={{ top: 20, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="nome" tick={{ fontSize: 11, fill: "#475569" }} interval={0} />
            <YAxis tick={{ fontSize: 11, fill: "#475569" }} unit="%" />
            <Tooltip formatter={(v) => `${num(v, 1)}%`} />
            <ReferenceLine y={0} stroke="#94A3B8" />
            <Bar dataKey="Credito al consumo Italia" fill={GRIGIO} radius={[3, 3, 0, 0]} />
            <Bar dataKey="DBE Srl" fill={TEAL} radius={[3, 3, 0, 0]}>
              <LabelList dataKey="DBE Srl" position="top" formatter={etichettaPerc}
                style={{ fontSize: 10, fill: TEAL, fontWeight: 700 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Legenda voci={[
        { label: "Credito al consumo in Italia (Banca d'Italia)", colore: GRIGIO },
        { label: "DBE Srl", colore: TEAL },
      ]} />
    </div>
  );
}

function TabellaProgressivo({ mesi }) {
  if (!mesi?.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[300px] border-collapse">
        <thead>
          <tr className="border-b-2 border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-3 text-left font-semibold">Progressivo a fine</th>
            <th className="py-2 px-3 text-right font-semibold">Mercato Italia</th>
            <th className="py-2 px-3 text-right font-semibold">DBE Srl</th>
            <th className="py-2 pl-3 text-right font-semibold">Differenza</th>
          </tr>
        </thead>
        <tbody>
          {mesi.map((m) => (
            <tr key={m.mese} className="border-b border-slate-100"
              data-testid={`progressivo-riga-${m.mese}`}>
              <td className="py-1.5 pr-3 text-sm text-slate-700">{m.mese_label}</td>
              <td className={`py-1.5 px-3 text-right text-sm font-bold ${tinta(m.mercato_var_progr_perc)}`}>
                {pp(m.mercato_var_progr_perc)}
              </td>
              <td className={`py-1.5 px-3 text-right text-sm font-bold ${tinta(m.dbe_var_progr_perc)}`}>
                {pp(m.dbe_var_progr_perc)}
              </td>
              <td className={`py-1.5 pl-3 text-right text-sm font-bold ${tinta(
                vuoto(m.dbe_var_progr_perc) || vuoto(m.mercato_var_progr_perc)
                  ? null : m.dbe_var_progr_perc - m.mercato_var_progr_perc)}`}>
                {vuoto(m.dbe_var_progr_perc) || vuoto(m.mercato_var_progr_perc)
                  ? "—" : ppp(Math.round((m.dbe_var_progr_perc - m.mercato_var_progr_perc) * 100) / 100)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Verdetto({ progressivo, anno }) {
  const p = progressivo || {};
  if (vuoto(p.delta_pp)) return null;
  const meglio = p.delta_pp >= 0;
  return (
    <div className={`h-fit rounded-xl border-2 p-4 ${meglio ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
      data-testid="verdetto-mercato">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">
        Progressivo da inizio anno · {p.mesi_confrontati} mesi confrontati
      </div>
      <div className={`mt-1 text-lg sm:text-xl font-black ${meglio ? "text-emerald-800" : "text-red-700"}`}>
        {meglio ? "Cresciamo più del mercato" : "Cresciamo meno del mercato"}
      </div>
      <div className="mt-2 text-sm text-slate-700">
        DBE Srl <strong>{pp(p.dbe_var_perc)}</strong> sul {anno - 1}, credito al consumo in Italia{" "}
        <strong>{pp(p.mercato_var_perc)}</strong>: differenza di{" "}
        <strong>{ppp(p.delta_pp)}</strong>.
      </div>
    </div>
  );
}

function TabellaMercato({ dati }) {
  const mesi = dati?.mesi || [];
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] border-collapse">
        <thead>
          <tr className="border-b-2 border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-3 text-left font-semibold">Mese</th>
            <th className="py-2 px-3 text-right font-semibold">Mercato {dati.anno_precedente}</th>
            <th className="py-2 px-3 text-right font-semibold">Mercato {dati.anno}</th>
            <th className="py-2 px-3 text-right font-semibold">Var. mercato</th>
            <th className="py-2 px-3 text-right font-semibold">DBE Srl {dati.anno_precedente}</th>
            <th className="py-2 px-3 text-right font-semibold">DBE Srl {dati.anno}</th>
            <th className="py-2 px-3 text-right font-semibold">Var. DBE Srl</th>
            <th className="py-2 pl-3 text-right font-semibold">Differenza</th>
          </tr>
        </thead>
        <tbody>
          {mesi.map((m) => (
            <tr key={m.mese} className="border-b border-slate-100"
              data-testid={`mercato-riga-${m.mese}`}>
              <td className="py-1.5 pr-3 text-sm text-slate-700">{m.mese_label}</td>
              <td className="py-1.5 px-3 text-right text-sm text-slate-600">{num(m.mercato_prec_mln, 0)} €M</td>
              <td className="py-1.5 px-3 text-right text-sm text-slate-800">{num(m.mercato_ora_mln, 0)} €M</td>
              <td className={`py-1.5 px-3 text-right text-sm font-bold ${tinta(m.mercato_var_perc)}`}>{pp(m.mercato_var_perc)}</td>
              <td className="py-1.5 px-3 text-right text-sm text-slate-600">{eurShort(m.dbe_prec)}</td>
              <td className="py-1.5 px-3 text-right text-sm text-slate-800">{eurShort(m.dbe_ora)}</td>
              <td className={`py-1.5 px-3 text-right text-sm font-bold ${tinta(m.dbe_var_perc)}`}>{pp(m.dbe_var_perc)}</td>
              <td className={`py-1.5 pl-3 text-right text-sm font-bold ${tinta(m.delta_pp)}`}>{ppp(m.delta_pp)}</td>
            </tr>
          ))}
          {!!mesi.length && (
            <tr className="border-t-2 border-slate-300 bg-slate-50" data-testid="mercato-riga-totale">
              <td className="py-2 pr-3 text-sm font-black text-slate-900">Progressivo</td>
              <td className="py-2 px-3 text-right text-sm text-slate-700">{num(dati.progressivo.mercato_prec_mln, 0)} €M</td>
              <td className="py-2 px-3 text-right text-sm font-bold text-slate-900">{num(dati.progressivo.mercato_ora_mln, 0)} €M</td>
              <td className={`py-2 px-3 text-right text-sm font-bold ${tinta(dati.progressivo.mercato_var_perc)}`}>{pp(dati.progressivo.mercato_var_perc)}</td>
              <td className="py-2 px-3 text-right text-sm text-slate-700">{eurShort(dati.progressivo.dbe_prec)}</td>
              <td className="py-2 px-3 text-right text-sm font-bold text-slate-900">{eurShort(dati.progressivo.dbe_ora)}</td>
              <td className={`py-2 px-3 text-right text-sm font-bold ${tinta(dati.progressivo.dbe_var_perc)}`}>{pp(dati.progressivo.dbe_var_perc)}</td>
              <td className={`py-2 pl-3 text-right text-sm font-black ${tinta(dati.progressivo.delta_pp)}`}>{ppp(dati.progressivo.delta_pp)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function VistaMercato({ mercato, articoli, puoScrivere, onAggiorna }) {
  const [meseSettori, setMeseSettori] = useState(articoli?.[0]
    ? `${articoli[0].anno}-${articoli[0].mese}` : "");
  const [caricando, setCaricando] = useState(false);
  const [progressivo, setProgressivo] = useState(null);
  const docSettori = useMemo(() => (articoli || []).find(
    (a) => `${a.anno}-${a.mese}` === meseSettori) || articoli?.[0], [articoli, meseSettori]);

  useEffect(() => {
    if (!docSettori) return;
    api.get(`/benchmark/confronto-progressivo/${docSettori.anno}?fino_a=${docSettori.mese}`)
      .then((r) => setProgressivo(r.data))
      .catch(() => setProgressivo(null));
  }, [docSettori]);

  const aggiorna = async () => {
    setCaricando(true);
    try {
      const r = await api.post("/benchmark/mercato/aggiorna");
      toast.success(`Serie Banca d'Italia aggiornata (${r.data.mesi} mesi)`);
      onAggiorna();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aggiornamento non riuscito");
    } finally {
      setCaricando(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="vista-mercato">
      <Card className="p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <SectionTitle>Scostamento sul budget: DBE Srl vs Deutsche Bank Easy</SectionTitle>
          <select value={meseSettori} onChange={(e) => setMeseSettori(e.target.value)}
            data-testid="select-mese-settori"
            className="rounded border border-slate-200 px-2 py-1 text-sm text-slate-800">
            {(articoli || []).map((a) => (
              <option key={`${a.anno}-${a.mese}`} value={`${a.anno}-${a.mese}`}>
                {MESI[a.mese - 1]} {a.anno}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-5 space-y-8">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500"
              data-testid="titolo-settori-mese">
              {docSettori ? `${MESI[docSettori.mese - 1]} ${docSettori.anno}` : "Mese"}
            </div>
            <GraficoSettori righe={docSettori?.confronto} testid="grafico-settori" />
            <div className="mt-4">
              <VerdettoBudget righe={docSettori?.confronto} testid="verdetto-budget-mese"
                etichetta={docSettori ? `${MESI[docSettori.mese - 1]} ${docSettori.anno}` : ""} />
            </div>
            <div className="mt-4"><TabellaConfronto righe={docSettori?.confronto} /></div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500"
              data-testid="titolo-settori-progressivo">
              Progressivo da inizio anno
              {progressivo?.etichetta ? ` · ${progressivo.etichetta}` : ""}
            </div>
            <GraficoSettori righe={progressivo?.righe} testid="grafico-settori-progressivo" />
            <div className="mt-4">
              <VerdettoBudget righe={progressivo?.righe} testid="verdetto-budget-progressivo"
                etichetta={`Progressivo${progressivo?.etichetta ? ` · ${progressivo.etichetta}` : ""}`} />
            </div>
            <div className="mt-4"><TabellaConfronto righe={progressivo?.righe} /></div>
          </div>
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <SectionTitle>Crescita sul {mercato.anno_precedente}: DBE Srl vs mercato italiano</SectionTitle>
          {puoScrivere && (
            <button type="button" onClick={aggiorna} disabled={caricando}
              data-testid="btn-aggiorna-mercato"
              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold
                text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900
                disabled:opacity-50">
              {caricando ? "Aggiornamento…" : "Aggiorna dati mercato"}
            </button>
          )}
        </div>
        <div className="mt-4 grid gap-5 lg:grid-cols-[2fr_1fr]">
          <GraficoCrescita mesi={mercato.mesi} />
          <Verdetto progressivo={mercato.progressivo} anno={mercato.anno} />
        </div>
        <div className="mt-5"><TabellaMercato dati={mercato} /></div>
        <p className="mt-2 text-[11px] text-slate-400">
          Fonte mercato: Banca d'Italia, nuove erogazioni di credito al consumo.
        </p>
      </Card>

      <Card className="p-5 sm:p-6">
        <SectionTitle>Progressivo da inizio anno</SectionTitle>
        <div className="mt-4 grid gap-5 lg:grid-cols-[3fr_2fr]">
          <GraficoProgressivo mesi={mercato.mesi} />
          <div className="space-y-4">
            <Verdetto progressivo={mercato.progressivo} anno={mercato.anno} />
            <TabellaProgressivo mesi={mercato.mesi} />
          </div>
        </div>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ caricamento newsletter

function Campo({ valore, onChange, testid, larghezza = "w-24" }) {
  return (
    <input type="text" inputMode="decimal" value={valore} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`${larghezza} rounded border border-slate-200 px-2 py-1 text-right text-sm
        focus:border-[#14284B] focus:outline-none`} />
  );
}

function Caricamento({ onChiudi, onPubblicato }) {
  const anno = new Date().getFullYear();
  const [bozza, setBozza] = useState(() => bozzaVuota(anno, new Date().getMonth() + 1));
  const [statoEstrazione, setStatoEstrazione] = useState("");
  const [fileId, setFileId] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const inputFile = useRef(null);

  const aggiornaLinea = (key, campo, v) => setBozza((s) => ({
    ...s, linee: { ...s.linee, [key]: { ...s.linee[key], [campo]: v } } }));

  const carica = async (file) => {
    if (!file) return;
    setStatoEstrazione("Lettura della newsletter in corso… può richiedere una trentina di secondi.");
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post("/benchmark/newsletter/estrai", form);
      const e = r.data.estratto || {};
      setFileId(r.data.file_id);
      setBozza({
        anno: e.anno || anno, mese: e.mese || 1,
        totale: { volumi_mln: testo(e.totale?.volumi_mln), var_budget_perc: testo(e.totale?.var_budget_perc), var_yoy_perc: testo(e.totale?.var_yoy_perc) },
        linee: Object.fromEntries(LINEE.map((l) => [l.key, {
          volumi_mln: testo(e.linee?.[l.key]?.volumi_mln),
          var_budget_perc: testo(e.linee?.[l.key]?.var_budget_perc),
          var_yoy_perc: testo(e.linee?.[l.key]?.var_yoy_perc),
        }])),
        rischio: { r30_perc: testo(e.rischio?.r30_perc), r30_delta_bps: testo(e.rischio?.r30_delta_bps), r90_perc: testo(e.rischio?.r90_perc), r90_delta_bps: testo(e.rischio?.r90_delta_bps) },
        testi: { intro: testo(e.testi?.intro), flash: testo(e.testi?.flash), progetti: testo(e.testi?.progetti) },
      });
      setStatoEstrazione("Valori estratti: controllali e correggi quelli sbagliati prima di pubblicare.");
    } catch (err) {
      setStatoEstrazione(err.response?.data?.detail
        || "Estrazione non riuscita: inserisci i valori a mano.");
    }
  };

  const pubblica = async () => {
    setSalvando(true);
    try {
      await api.post("/benchmark/newsletter", { ...bozza, file_id: fileId });
      toast.success(`Articolo di ${MESI[bozza.mese - 1]} ${bozza.anno} pubblicato`);
      onPubblicato(bozza.anno, bozza.mese);
      onChiudi();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Pubblicazione non riuscita");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Card className="p-5 sm:p-6" data-testid="pannello-caricamento">
      <div className="flex items-start justify-between gap-3">
        <SectionTitle>Carica una newsletter</SectionTitle>
        <button type="button" onClick={onChiudi} data-testid="chiudi-caricamento"
          className="text-xs font-semibold text-slate-500 hover:text-slate-900">Chiudi</button>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Il mese indicato è quello <strong>dei dati</strong>, non della data di invio.
        Puoi caricare anche i mesi passati.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input ref={inputFile} type="file" accept=".png,.jpg,.jpeg,.webp,.pdf"
          onChange={(e) => carica(e.target.files?.[0])} className="hidden"
          data-testid="input-file-newsletter" />
        <button type="button" onClick={() => inputFile.current?.click()}
          data-testid="btn-scegli-file"
          className="rounded-full px-4 py-2 text-sm font-bold text-white transition-transform
            hover:-translate-y-0.5" style={{ background: BLU }}>
          Scegli immagine o PDF
        </button>
        <label className="text-xs text-slate-500">
          Mese
          <select value={bozza.mese} data-testid="select-mese"
            onChange={(e) => setBozza((s) => ({ ...s, mese: Number(e.target.value) }))}
            className="ml-2 rounded border border-slate-200 px-2 py-1 text-sm text-slate-800">
            {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        <label className="text-xs text-slate-500">
          Anno
          <input type="number" value={bozza.anno} data-testid="input-anno"
            onChange={(e) => setBozza((s) => ({ ...s, anno: Number(e.target.value) }))}
            className="ml-2 w-24 rounded border border-slate-200 px-2 py-1 text-sm text-slate-800" />
        </label>
      </div>
      {statoEstrazione && (
        <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-600"
          data-testid="stato-estrazione">{statoEstrazione}</div>
      )}

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[620px] border-collapse">
          <thead>
            <tr className="border-b-2 border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
              <th className="py-2 pr-3 text-left font-semibold">Voce</th>
              <th className="py-2 px-3 text-right font-semibold">Volumi €M</th>
              <th className="py-2 px-3 text-right font-semibold">% vs budget</th>
              <th className="py-2 pl-3 text-right font-semibold">% yoy</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-100">
              <td className="py-2 pr-3 text-sm font-bold text-slate-900">
                Totale Deutsche Bank Easy
              </td>
              {["volumi_mln", "var_budget_perc", "var_yoy_perc"].map((c) => (
                <td key={c} className="py-2 px-3 text-right">
                  <Campo valore={bozza.totale[c]} testid={`campo-totale-${c}`}
                    onChange={(v) => setBozza((s) => ({ ...s, totale: { ...s.totale, [c]: v } }))} />
                </td>
              ))}
            </tr>
            {LINEE.map((l) => (
              <tr key={l.key} className="border-b border-slate-100">
                <td className={`py-2 pr-3 text-sm ${l.figlio ? "pl-6 text-slate-600" : "font-semibold text-slate-900"}`}>
                  {l.label}
                </td>
                {["volumi_mln", "var_budget_perc", "var_yoy_perc"].map((c) => (
                  <td key={c} className="py-2 px-3 text-right">
                    <Campo valore={bozza.linee[l.key][c]} testid={`campo-${l.key}-${c}`}
                      onChange={(v) => aggiornaLinea(l.key, c, v)} />
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td className="py-2 pr-3 text-sm font-semibold text-slate-900">Rischio 30+ / 90+</td>
              <td className="py-2 px-3 text-right">
                <Campo valore={bozza.rischio.r30_perc} testid="campo-r30"
                  onChange={(v) => setBozza((s) => ({ ...s, rischio: { ...s.rischio, r30_perc: v } }))} />
              </td>
              <td className="py-2 px-3 text-right">
                <Campo valore={bozza.rischio.r30_delta_bps} testid="campo-r30-bps"
                  onChange={(v) => setBozza((s) => ({ ...s, rischio: { ...s.rischio, r30_delta_bps: v } }))} />
              </td>
              <td className="py-2 pl-3 text-right">
                <div className="flex justify-end gap-2">
                  <Campo valore={bozza.rischio.r90_perc} testid="campo-r90" larghezza="w-20"
                    onChange={(v) => setBozza((s) => ({ ...s, rischio: { ...s.rischio, r90_perc: v } }))} />
                  <Campo valore={bozza.rischio.r90_delta_bps} testid="campo-r90-bps" larghezza="w-20"
                    onChange={(v) => setBozza((s) => ({ ...s, rischio: { ...s.rischio, r90_delta_bps: v } }))} />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button type="button" onClick={pubblica} disabled={salvando} data-testid="btn-pubblica"
        className="mt-5 rounded-full px-5 py-2 text-sm font-bold text-white transition-transform
          hover:-translate-y-0.5 disabled:opacity-50" style={{ background: VERDE }}>
        {salvando ? "Pubblicazione…" : "Pubblica articolo"}
      </button>
    </Card>
  );
}

// ------------------------------------------------------------------ pagina

function VoceSidebar({ attiva, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
        attiva ? "bg-[#14284B] font-bold text-white" : "text-slate-600 hover:bg-slate-100"}`}>
      {children}
    </button>
  );
}

export default function MercatoBenchmark() {
  const { user } = useAuth();
  const puoScrivere = ["MASTER", "ADMIN"].includes(user?.role);
  const [dati, setDati] = useState(null);
  const [mercato, setMercato] = useState(null);
  const [errore, setErrore] = useState("");
  const [aperto, setAperto] = useState(false);
  const [vista, setVista] = useState("mercato");
  const [selezione, setSelezione] = useState(null);

  const carica = useCallback(async (anno, mese) => {
    try {
      const [a, m] = await Promise.all([
        api.get("/benchmark/newsletter"),
        api.get("/benchmark/mercato-confronto"),
      ]);
      setDati(a.data);
      setMercato(m.data);
      const primi = a.data.articoli || [];
      setSelezione((prec) => {
        if (anno && mese) return `${anno}-${mese}`;
        if (prec && primi.some((x) => `${x.anno}-${x.mese}` === prec)) return prec;
        return primi.length ? `${primi[0].anno}-${primi[0].mese}` : null;
      });
    } catch (e) {
      setErrore(e.response?.data?.detail || "Caricamento non riuscito");
    }
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const articoli = dati?.articoli || [];
  const corrente = articoli.find((a) => `${a.anno}-${a.mese}` === selezione) || articoli[0];

  const modifica = async (doc, articolo) => {
    try {
      await api.put(`/benchmark/newsletter/${doc.anno}/${doc.mese}/articolo`, { articolo });
      toast.success("Testo aggiornato");
      carica(doc.anno, doc.mese);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Modifica non riuscita");
    }
  };
  const rigenera = async (doc) => {
    try {
      await api.post(`/benchmark/newsletter/${doc.anno}/${doc.mese}/rigenera`);
      toast.success("Commento rigenerato");
      carica(doc.anno, doc.mese);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rigenerazione non riuscita");
    }
  };
  const elimina = async (doc) => {
    if (!window.confirm(`Eliminare l'articolo di ${MESI[doc.mese - 1]} ${doc.anno}?`)) return;
    try {
      await api.delete(`/benchmark/newsletter/${doc.anno}/${doc.mese}`);
      toast.success("Articolo eliminato");
      setSelezione(null);
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  if (errore) {
    return <div className="p-6 text-sm text-red-600" data-testid="benchmark-errore">{errore}</div>;
  }
  return (
    <div className="space-y-6" data-testid="pagina-benchmark">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <TornaHome testId="btn-benchmark-home" />
          <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight" style={{ color: BLU }}>
            Mercato / Benchmark
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Il posizionamento di DBE Srl rispetto a Deutsche Bank Easy e al mercato italiano.
          </p>
        </div>
        {puoScrivere && !aperto && (
          <button type="button" onClick={() => setAperto(true)} data-testid="btn-apri-caricamento"
            className="rounded-full px-5 py-2.5 text-sm font-bold text-white transition-transform
              hover:-translate-y-0.5" style={{ background: ROSSO }}>
            Carica newsletter
          </button>
        )}
      </div>

      {aperto && <Caricamento onChiudi={() => setAperto(false)} onPubblicato={carica} />}

      <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
        <Card className="h-fit p-3" data-testid="sidebar-benchmark">
          <VoceSidebar attiva={vista === "mercato"} testid="voce-dbe-vs-mercato"
            onClick={() => setVista("mercato")}>
            DBE Srl vs mercato
          </VoceSidebar>
          <div className="mt-3 px-3 text-[11px] uppercase tracking-wide text-slate-400">
            Newsletter
          </div>
          <div className="mt-1 space-y-0.5">
            {!articoli.length && (
              <p className="px-3 py-2 text-xs text-slate-400" data-testid="sidebar-vuota">
                Nessuna newsletter
              </p>
            )}
            {articoli.map((a) => (
              <VoceSidebar key={`${a.anno}-${a.mese}`}
                testid={`voce-newsletter-${a.anno}-${a.mese}`}
                attiva={vista === "newsletter" && `${a.anno}-${a.mese}` === selezione}
                onClick={() => { setVista("newsletter"); setSelezione(`${a.anno}-${a.mese}`); }}>
                {MESI[a.mese - 1]} {a.anno}
              </VoceSidebar>
            ))}
          </div>
        </Card>

        <div className="min-w-0">
          {!dati || !mercato ? (
            <div className="text-sm text-slate-500" data-testid="benchmark-caricamento">
              Caricamento…
            </div>
          ) : vista === "mercato" ? (
            <VistaMercato mercato={mercato} articoli={articoli} puoScrivere={puoScrivere}
              onAggiorna={carica} />
          ) : corrente ? (
            <Articolo doc={corrente} puoScrivere={puoScrivere} onModifica={modifica}
              onElimina={elimina} onRigenera={rigenera} />
          ) : (
            <Card className="p-6 text-sm text-slate-500" data-testid="benchmark-vuoto">
              Nessuna newsletter caricata. Carica la prima per generare l'articolo del mese.
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
